import React, { useState, useContext, useEffect } from "react";
import { makeStyles } from "@material-ui/core/styles";
import Paper from "@material-ui/core/Paper";
import Button from "@material-ui/core/Button";
import Typography from "@material-ui/core/Typography";
import FormControl from "@material-ui/core/FormControl";
import FormControlLabel from "@material-ui/core/FormControlLabel";
import Checkbox from "@material-ui/core/Checkbox";
import InputLabel from "@material-ui/core/InputLabel";
import MenuItem from "@material-ui/core/MenuItem";
import Select from "@material-ui/core/Select";
import CircularProgress from "@material-ui/core/CircularProgress";
import Box from "@material-ui/core/Box";
import Chip from "@material-ui/core/Chip";
import Dialog from "@material-ui/core/Dialog";
import DialogTitle from "@material-ui/core/DialogTitle";
import DialogContent from "@material-ui/core/DialogContent";
import DialogActions from "@material-ui/core/DialogActions";
import moment from "moment";
import { toast } from "react-toastify";

import ExitToAppIcon from "@material-ui/icons/ExitToApp";

import MainContainer from "../../components/MainContainer";
import MainHeader from "../../components/MainHeader";
import MainHeaderButtonsWrapper from "../../components/MainHeaderButtonsWrapper";
import Title from "../../components/Title";
import ForbiddenPage from "../../components/ForbiddenPage";
import api from "../../services/api";
import toastError from "../../errors/toastError";
import { AuthContext } from "../../context/Auth/AuthContext";
import usePlans from "../../hooks/usePlans";
import { i18n } from "../../translate/i18n";
import parsePlanAmount from "../../utils/parsePlanAmount";
import {
  allowedPaymentMethods as getAllowedPaymentMethods,
  maxInstallmentCount as getMaxInstallmentCount,
} from "../../utils/planMetadata";

const useStyles = makeStyles((theme) => ({
  mainPaper: {
    flex: 1,
    padding: theme.spacing(4),
    margin: theme.spacing(1),
    overflowY: "auto",
    ...theme.scrollbarStyles,
  },
  widePopover: {
    maxWidth: "none !important",
    minWidth: "340px !important",
  },
  planCard: {
    padding: theme.spacing(3),
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: theme.shape.borderRadius,
    marginBottom: theme.spacing(3),
  },
  expiredBanner: {
    backgroundColor: "#ffebee",
    color: "#c62828",
    padding: theme.spacing(2),
    borderRadius: theme.shape.borderRadius,
    marginBottom: theme.spacing(3),
    textAlign: "center",
  },
  trialBanner: {
    backgroundColor: "#fff3e0",
    color: "#e65100",
    padding: theme.spacing(2),
    borderRadius: theme.shape.borderRadius,
    marginBottom: theme.spacing(3),
    textAlign: "center",
  },
  featureChip: {
    margin: theme.spacing(0.5),
  },
  formControl: {
    minWidth: 280,
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(2),
  },
  payButton: {
    marginTop: theme.spacing(2),
    padding: theme.spacing(1.5, 4),
  },
}));

const formatCurrency = (value) => {
  if (!Number.isFinite(value)) return "0,00";
  return value
    .toFixed(2)
    .replace(".", ",")
    .replace(/\B(?=(\d{3})+(?!\d))/g, ".");
};

const PAYMENT_LABELS = {
  PIX: "PIX",
  BOLETO: "Boleto Bancário",
  CREDIT_CARD: "Cartão de Crédito",
};

const buildFeatureList = (plan) => {
  if (!plan) return [];
  const features = [];
  features.push(`${plan.users} usuário(s)`);
  features.push(`${plan.connections} conexão(ões)`);
  features.push(`${plan.queues} fila(s)`);
  if (plan.useWhatsapp) features.push("WhatsApp");
  if (plan.useFacebook) features.push("Facebook");
  if (plan.useInstagram) features.push("Instagram");
  if (plan.useCampaigns) features.push("Campanhas");
  if (plan.useOpenAi) features.push("Talk.Ai");
  if (plan.useKanban) features.push("CRM");
  if (plan.useInternalChat) features.push("Chat Interno");
  if (plan.useExternalApi) features.push("API Externa");
  return features;
};

const Subscription = () => {
  const classes = useStyles();
  const { user, handleLogout } = useContext(AuthContext);
  const { list: listPlans } = usePlans();

  const [paymentFlow, setPaymentFlow] = useState("");
  const [installmentCount, setInstallmentCount] = useState("");
  const [renewalConsent, setRenewalConsent] = useState(false);
  const [loading, setLoading] = useState(false);
  const [cancelDialogOpen, setCancelDialogOpen] = useState(false);
  const [cancelLoading, setCancelLoading] = useState(false);
  const [reactivateDialogOpen, setReactivateDialogOpen] = useState(false);
  const [reactivateLoading, setReactivateLoading] = useState(false);

  // Plan picker state (for trial users)
  const [availablePlans, setAvailablePlans] = useState([]);
  const [selectedPlanId, setSelectedPlanId] = useState("");
  const [loadingPlans, setLoadingPlans] = useState(false);

  const company = user?.company;
  const companyPlan = company?.plan;
  const dueDate = company?.dueDate;
  const isTrial = !companyPlan || companyPlan.trial;
  // Backend marca status=false em qualquer cenário sem assinatura ativa
  // (cadastro abandonado, vencimento, reembolso, chargeback). Nesses casos
  // permitimos que o usuário escolha (ou troque de) plano antes de pagar.
  const isInactive = company?.status === false;
  const canPickPlan = isTrial || isInactive;

  const isExpired = dueDate
    ? moment().startOf("day").isAfter(moment(dueDate).startOf("day"), "day")
    : false;

  // Fetch paid plans whenever the user needs to (re)pick one.
  useEffect(() => {
    if (!canPickPlan) return;
    let active = true;
    const fetchPlans = async () => {
      setLoadingPlans(true);
      try {
        const plans = await listPlans();
        if (active) {
          const paidPlans = (plans || []).filter(
            (p) => !p.trial && parsePlanAmount(p.amount) > 0
          );
          setAvailablePlans(paidPlans);
        }
      } catch (err) {
        toastError(err);
      }
      if (active) setLoadingPlans(false);
    };
    fetchPlans();
    return () => { active = false; };
  }, [canPickPlan]); // eslint-disable-line react-hooks/exhaustive-deps

  // For inactive (non-trial) users, default the dropdown to the plan they
  // already signed up for so they can just hit "Pagar Agora" without
  // re-selecting; they can still switch to a different plan if they want.
  useEffect(() => {
    if (
      isInactive &&
      !isTrial &&
      !selectedPlanId &&
      companyPlan?.id &&
      availablePlans.some((p) => p.id === companyPlan.id)
    ) {
      setSelectedPlanId(companyPlan.id);
    }
  }, [isInactive, isTrial, selectedPlanId, companyPlan, availablePlans]);

  // The "active" plan: when the user can pick one, use the dropdown selection
  // (falling back to the company's current paid plan for inactive users so
  // the form renders even before the default-selection effect fires);
  // otherwise stick with the company's plan.
  const activePlan = canPickPlan
    ? availablePlans.find((p) => p.id === selectedPlanId) ||
      (isInactive && !isTrial ? companyPlan : null)
    : companyPlan;

  const allowedMethods = getAllowedPaymentMethods(activePlan);
  const maxInstallments = getMaxInstallmentCount(activePlan);

  // Reset payment flow + installments when plan changes
  useEffect(() => {
    setPaymentFlow("");
    setInstallmentCount("");
  }, [selectedPlanId]);

  // Reset installments when switching payment method
  useEffect(() => {
    setInstallmentCount("");
  }, [paymentFlow]);

  // Auto-select if only one payment method
  useEffect(() => {
    if (allowedMethods.length === 1 && !paymentFlow) {
      setPaymentFlow(allowedMethods[0]);
    }
  }, [allowedMethods, paymentFlow]);

  const planAmount = activePlan ? parsePlanAmount(activePlan.amount) : 0;
  // For CC-only Semestral/Anual, plan.amount is the per-month price at max N,
  // not the total. Mirrors backend getPlanTotalValue().
  const planTotalValue = maxInstallments
    ? Math.round(planAmount * maxInstallments * 100) / 100
    : planAmount;

  const planFeatures = buildFeatureList(activePlan);
  const showInstallmentPicker =
    !!maxInstallments && paymentFlow === "CREDIT_CARD";

  const handlePay = async () => {
    if (!paymentFlow) {
      toast.warn("Selecione uma forma de pagamento.");
      return;
    }
    if (showInstallmentPicker && !installmentCount) {
      toast.warn(i18n.t("subscription.warnings.pickInstallments"));
      return;
    }
    if (!renewalConsent) {
      toast.warn(
        "Você precisa concordar com os termos de renovação automática."
      );
      return;
    }

    setLoading(true);
    try {
      const payload = { paymentFlow };
      // Send planId whenever the user could have picked one. Backend reuses
      // the existing Asaas entity when planId matches company.planId, so it's
      // safe to always include it.
      if (canPickPlan && activePlan) {
        payload.planId = activePlan.id;
      }
      if (showInstallmentPicker && installmentCount) {
        payload.installmentCount = Number(installmentCount);
      }
      const { data } = await api.post("/billing/create-checkout", payload);
      if (data.checkoutUrl) {
        toast.success("Redirecionando para o pagamento...");
        window.location.href = data.checkoutUrl;
      }
    } catch (err) {
      toastError(err);
      setLoading(false);
    }
  };

  // Cancel / reactivate / note visibility:
  //   PIX / Boleto (any cycle): no auto-charge to stop — show an info
  //     note instead of any button. Customer cancels by not paying.
  //   CC + auto-renewing: show cancel button.
  //   CC + cancelled + Sem/Anu (Group C/D): cancel was soft (kept token);
  //     show reactivate button — backend flips autoRenewEnabled back to
  //     true and the renewal cron picks it up at the locked dueDate.
  //   CC + cancelled + Mensal (Group A): cancel was hard (Asaas
  //     Subscription deleted, token nulled); no quick reactivation path,
  //     customer waits for wall and re-ups via the walled garden.
  const currentPaymentFlow = company?.paymentFlow;
  const isCreditCard = currentPaymentFlow === "CREDIT_CARD";
  const isPixOrBoleto =
    currentPaymentFlow === "PIX" || currentPaymentFlow === "BOLETO";
  const planRecurrence = (
    companyPlan?.recurrence ||
    company?.recurrence ||
    ""
  ).toUpperCase();
  const isMensal = planRecurrence === "MENSAL";
  const isAutoRenewing = !!company?.autoRenewEnabled;
  const showCancelButton = isCreditCard && isAutoRenewing;
  const showReactivateButton =
    isCreditCard && !isAutoRenewing && !isMensal;
  const showPixBoletoNote = isPixOrBoleto;

  const handleCancelSubscription = async () => {
    setCancelLoading(true);
    try {
      const { data } = await api.post("/billing/cancel-subscription");
      const accessUntil = data?.accessUntil
        ? moment(data.accessUntil).format("DD/MM/YYYY")
        : null;
      toast.success(
        accessUntil
          ? i18n.t("subscription.cancel.successWithDate", { date: accessUntil })
          : i18n.t("subscription.cancel.success")
      );
      setCancelDialogOpen(false);
      // Reload so the company.asaasSubscriptionId reset is reflected in the
      // user context (and the cancel button hides itself).
      window.location.reload();
    } catch (err) {
      toastError(err);
      setCancelLoading(false);
    }
  };

  const handleReactivateSubscription = async () => {
    setReactivateLoading(true);
    try {
      const { data } = await api.post("/billing/reactivate-subscription");
      const renewsOnLabel = data?.renewsOn
        ? moment(data.renewsOn).format("DD/MM/YYYY")
        : null;
      toast.success(
        renewsOnLabel
          ? i18n.t("subscription.reactivate.successWithDate", { date: renewsOnLabel })
          : i18n.t("subscription.reactivate.success")
      );
      setReactivateDialogOpen(false);
      // Reload so company.autoRenewEnabled=true is reflected in the user
      // context (and the reactivate button hides itself, cancel returns).
      window.location.reload();
    } catch (err) {
      toastError(err);
      setReactivateLoading(false);
    }
  };

  // --- Render helpers ---

  const renderPlanCard = () => (
    <div className={classes.planCard}>
      <Typography variant="h6" gutterBottom>
        {activePlan.name}
      </Typography>
      <Typography variant="h4" color="primary" gutterBottom>
        R$ {formatCurrency(planTotalValue)}
      </Typography>
      <Box mt={1}>
        {planFeatures.map((feature) => (
          <Chip
            key={feature}
            label={feature}
            size="small"
            className={classes.featureChip}
          />
        ))}
      </Box>
    </div>
  );

  const renderInstallmentPicker = () => (
    <div>
      <FormControl variant="outlined" className={classes.formControl}>
        <InputLabel>{i18n.t("subscription.form.installments")}</InputLabel>
        <Select
          value={installmentCount}
          onChange={(e) => setInstallmentCount(e.target.value)}
          label={i18n.t("subscription.form.installments")}
        >
          {Array.from({ length: maxInstallments }, (_, i) => {
            const count = i + 1;
            const perInstallment = planTotalValue / count;
            const key =
              count === 1
                ? "subscription.form.installmentsOptionSingle"
                : "subscription.form.installmentsOption";
            return (
              <MenuItem key={count} value={count}>
                {i18n.t(key, { count, value: formatCurrency(perInstallment) })}
              </MenuItem>
            );
          })}
        </Select>
      </FormControl>
      <Typography variant="caption" color="textSecondary" style={{ display: "block" }}>
        {i18n.t("subscription.form.installmentsHelper", { max: maxInstallments })}
      </Typography>
    </div>
  );

  const renderActivePlanInfo = () => {
    const recurrenceLabel = (() => {
      const r = (companyPlan?.recurrence || company?.recurrence || "")
        .toUpperCase();
      if (r === "MENSAL") return "Mensal";
      if (r === "SEMESTRAL") return "Semestral";
      if (r === "ANUAL") return "Anual";
      return r || "—";
    })();
    const paymentLabel =
      PAYMENT_LABELS[company?.paymentFlow] || company?.paymentFlow || "—";
    const nextDueLabel = dueDate
      ? moment(dueDate).format("DD/MM/YYYY")
      : "—";
    const monthlyValue = parsePlanAmount(companyPlan?.amount || 0);

    return (
      <div className={classes.planCard}>
        <Box display="flex" alignItems="center" mb={1}>
          <Typography variant="h6" style={{ marginRight: 8 }}>
            {companyPlan?.name}
          </Typography>
          <Chip
            label="Ativo"
            size="small"
            style={{ backgroundColor: "#e8f5e9", color: "#2e7d32" }}
          />
        </Box>
        <Typography variant="h4" color="primary" gutterBottom>
          R$ {formatCurrency(monthlyValue)}
        </Typography>
        <Box mt={1} mb={2}>
          <Typography variant="body2" color="textSecondary">
            Recorrência: {recurrenceLabel}
          </Typography>
          <Typography variant="body2" color="textSecondary">
            Forma de pagamento: {paymentLabel}
          </Typography>
          <Typography variant="body2" color="textSecondary">
            Próximo vencimento: {nextDueLabel}
          </Typography>
        </Box>
        <Box>
          {planFeatures.map((feature) => (
            <Chip
              key={feature}
              label={feature}
              size="small"
              className={classes.featureChip}
            />
          ))}
        </Box>
        {showCancelButton && (
          <Box mt={3}>
            <Button
              variant="outlined"
              color="secondary"
              onClick={() => setCancelDialogOpen(true)}
            >
              {i18n.t("subscription.cancel.button")}
            </Button>
          </Box>
        )}
        {showReactivateButton && (
          <Box mt={3}>
            <Button
              variant="contained"
              color="primary"
              onClick={() => setReactivateDialogOpen(true)}
            >
              {i18n.t("subscription.reactivate.button")}
            </Button>
          </Box>
        )}
        {showPixBoletoNote && (
          <Box mt={3}>
            <Typography variant="body2" color="textSecondary">
              {i18n.t("subscription.pixBoletoNote")}
            </Typography>
          </Box>
        )}
      </div>
    );
  };

  const renderReactivateDialog = () => {
    const renewsOnLabel = dueDate
      ? moment(dueDate).format("DD/MM/YYYY")
      : "—";
    return (
      <Dialog
        open={reactivateDialogOpen}
        onClose={() => !reactivateLoading && setReactivateDialogOpen(false)}
      >
        <DialogTitle>{i18n.t("subscription.reactivate.title")}</DialogTitle>
        <DialogContent>
          <Typography>
            {i18n.t("subscription.reactivate.body", { date: renewsOnLabel })}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setReactivateDialogOpen(false)}
            disabled={reactivateLoading}
          >
            {i18n.t("subscription.reactivate.back")}
          </Button>
          <Button
            onClick={handleReactivateSubscription}
            color="primary"
            disabled={reactivateLoading}
          >
            {reactivateLoading ? (
              <CircularProgress size={20} color="inherit" />
            ) : (
              i18n.t("subscription.reactivate.confirmButton")
            )}
          </Button>
        </DialogActions>
      </Dialog>
    );
  };

  const renderCancelDialog = () => {
    const accessUntilLabel = dueDate
      ? moment(dueDate).format("DD/MM/YYYY")
      : "—";
    return (
      <Dialog
        open={cancelDialogOpen}
        onClose={() => !cancelLoading && setCancelDialogOpen(false)}
      >
        <DialogTitle>{i18n.t("subscription.cancel.title")}</DialogTitle>
        <DialogContent>
          <Typography>
            {i18n.t("subscription.cancel.body", { date: accessUntilLabel })}
          </Typography>
          <Typography style={{ marginTop: 16 }}>
            {i18n.t("subscription.cancel.confirm")}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setCancelDialogOpen(false)}
            disabled={cancelLoading}
          >
            {i18n.t("subscription.cancel.back")}
          </Button>
          <Button
            onClick={handleCancelSubscription}
            color="secondary"
            disabled={cancelLoading}
          >
            {cancelLoading ? (
              <CircularProgress size={20} color="inherit" />
            ) : (
              i18n.t("subscription.cancel.confirmButton")
            )}
          </Button>
        </DialogActions>
      </Dialog>
    );
  };

  const renderPaymentMethodPicker = () => (
    <div>
      <Typography variant="subtitle1" gutterBottom>
        Forma de pagamento
      </Typography>

      {allowedMethods.length === 0 ? (
        <Typography color="error">
          Nenhuma forma de pagamento disponível para este plano.
          Entre em contato com o suporte.
        </Typography>
      ) : allowedMethods.length === 1 ? (
        <Typography variant="body1" gutterBottom>
          {PAYMENT_LABELS[allowedMethods[0]] || allowedMethods[0]}
        </Typography>
      ) : (
        <FormControl variant="outlined" className={classes.formControl}>
          <InputLabel>Forma de pagamento</InputLabel>
          <Select
            value={paymentFlow}
            onChange={(e) => setPaymentFlow(e.target.value)}
            label="Forma de pagamento"
          >
            {allowedMethods.map((method) => (
              <MenuItem key={method} value={method}>
                {PAYMENT_LABELS[method] || method}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      )}
    </div>
  );

  if (user?.profile !== "admin") {
    return <ForbiddenPage />;
  }

  return (
    <MainContainer>
      <MainHeader>
        <Title>Assinatura</Title>
        <MainHeaderButtonsWrapper>
          <Button
            variant="outlined"
            color="default"
            startIcon={<ExitToAppIcon />}
            onClick={handleLogout}
          >
            {i18n.t("mainDrawer.appBar.user.logout")}
          </Button>
        </MainHeaderButtonsWrapper>
      </MainHeader>
      <Paper className={classes.mainPaper} variant="outlined">
        {isInactive && !isTrial && (
          <div className={classes.expiredBanner}>
            <Typography variant="subtitle1">
              {isExpired
                ? `Sua assinatura venceu em ${moment(dueDate).format(
                    "DD/MM/YYYY"
                  )}. Renove para continuar usando o sistema.`
                : "Conclua sua assinatura para começar a usar o sistema."}
            </Typography>
          </div>
        )}

        {isTrial && (
          <div className={classes.trialBanner}>
            <Typography variant="subtitle1">
              {isExpired
                ? "Seu período de teste expirou. Escolha um plano para continuar usando o sistema."
                : "Você está em um plano de teste. Escolha um plano pago para garantir acesso contínuo."}
            </Typography>
          </div>
        )}

        {canPickPlan ? (
          // Trial OR inactive (abandoned signup / expired / refunded): plan picker
          loadingPlans ? (
            <Box textAlign="center" py={4}>
              <CircularProgress size={36} />
              <Typography variant="body2" color="textSecondary" style={{ marginTop: 16 }}>
                Carregando planos disponíveis...
              </Typography>
            </Box>
          ) : availablePlans.length === 0 ? (
            <Typography>
              Nenhum plano disponível no momento. Entre em contato com o suporte.
            </Typography>
          ) : (
            <>
              <FormControl
                variant="outlined"
                className={classes.formControl}
                fullWidth
              >
                <InputLabel>Escolha um plano</InputLabel>
                <Select
                  value={selectedPlanId}
                  onChange={(e) => setSelectedPlanId(e.target.value)}
                  label="Escolha um plano"
                  autoWidth
                  MenuProps={{ classes: { paper: classes.widePopover } }}
                >
                  {availablePlans.map((p) => (
                    <MenuItem key={p.id} value={p.id}>
                      {p.name} — R${" "}
                      {parsePlanAmount(p.amount).toFixed(2).replace(".", ",")}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              {activePlan && (
                <>
                  {renderPlanCard()}
                  {renderPaymentMethodPicker()}
                  {showInstallmentPicker && renderInstallmentPicker()}

                  <div>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={renewalConsent}
                          onChange={(e) => setRenewalConsent(e.target.checked)}
                          color="primary"
                        />
                      }
                      label={
                        <Typography variant="body2">
                          Ao confirmar, você concorda que seu plano será renovado
                          automaticamente.
                        </Typography>
                      }
                    />
                  </div>

                  <Button
                    variant="contained"
                    color="primary"
                    size="large"
                    className={classes.payButton}
                    onClick={handlePay}
                    disabled={loading || !paymentFlow || !renewalConsent || (showInstallmentPicker && !installmentCount)}
                  >
                    {loading ? (
                      <CircularProgress size={24} color="inherit" />
                    ) : (
                      "Pagar Agora"
                    )}
                  </Button>
                </>
              )}
            </>
          )
        ) : activePlan ? (
          // Active paid user: read-only plan info. Plan changes happen only
          // via the walled-garden flow once their term ends and they become
          // inactive — by design (boss decision, see project memory).
          renderActivePlanInfo()
        ) : (
          <Typography>
            Nenhum plano associado à sua empresa. Entre em contato com o
            suporte.
          </Typography>
        )}
      </Paper>
      {renderCancelDialog()}
      {renderReactivateDialog()}
    </MainContainer>
  );
};

export default Subscription;
